import { useApplicantAPI, useRequest } from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import { showToast } from "@certn/cascadia";
import { Loader } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { addDays } from "date-fns";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTimeOfDay } from "./hooks";
import { compareActiveCases, disableDeclineCase } from "./utils";
import { checksThatCannotBeDeclined } from "./constants";
import { useProfileName } from "../../auth/ProfileProvider";
import { AppTitle } from "../../components/AppTitle";
import { EmptyState } from "../../components/empty-state/EmptyState";
import { PortalCard } from "../../components/portal-card/PortalCard";
import { informationRequestPagePath } from "../information-request/constants";
import { useOutstandingInformationRequests } from "../information-request/hooks";
import {
  countUnsubmittedItems,
  getOldestSentAt,
} from "../information-request/utils";

export type Case = components["schemas"]["OpenCaseList"];

const getCardType = ({ applicant_status }: Case) => {
  switch (applicant_status) {
    case "APPLICANT_EXPIRED":
      return "expired";
    case "CASE_ORDERED":
    case "APPLICANT_INVITED":
    case "APPLICANT_OPENED":
      return "ready";
    case "APPLICANT_STARTED":
      return "progress";
    case "APPLICANT_SUBMITTED":
      return "review";
    default:
      return "ready";
  }
};

export function DashboardCards() {
  const activeCaseStatus = [
    "CASE_ORDERED",
    "APPLICANT_INVITED",
    "APPLICANT_OPENED",
    "APPLICANT_STARTED",
  ];
  const { t } = useTranslation();

  const { name, refresh } = useProfileName();

  useEffect(() => {
    refresh();
  }, [refresh]);

  const { data: openCases, mutate: refreshOpenCases } = useApplicantAPI(
    "/api/internal/applicant/case/open/"
  );
  const { data: completeCases, mutate: refreshCompleteCases } = useApplicantAPI(
    "/api/internal/applicant/case/complete/"
  );

  const { data: notifications } = useApplicantAPI(
    "/api/internal/applicant/case/notifications/"
  );
  const informationRequestCases = (completeCases ?? []).filter(
    (caseItem) => caseItem.has_outstanding_information_requests
  );
  const { data: outstandingRequests, isLoading: outstandingRequestsLoading } =
    useOutstandingInformationRequests(
      informationRequestCases.map((c) => c.short_id)
    );
  const informationRequestCards = informationRequestCases.flatMap(
    (caseItem) => {
      const requests =
        outstandingRequests?.find(({ caseId }) => caseId === caseItem.short_id)
          ?.requests ?? [];
      return requests.length ? [{ caseItem, requests }] : [];
    }
  );
  const navigate = useNavigate();
  const request = useRequest();

  const timeGreeting = useTimeOfDay();
  const fullGreeting = `${timeGreeting}, ${name.givenName}`;

  const smallNotif: { [caseId: string]: string } =
    notifications?.reduce(
      (
        smallNotif: { [caseId: string]: string },
        notification: {
          type: string;
          context: Record<string, unknown> | null;
          created: string;
        }
      ) => {
        if (
          notification.type === "DCO_REVIEW" &&
          notification.context &&
          typeof notification.context.case_id === "string" &&
          notification.context.review_completed === false &&
          new Date() <=
            addDays(
              new Date(
                notification.context.notification_iso_datetime as string
              ),
              2
            )
        ) {
          smallNotif[notification.context.case_id] = notification.context
            .notification_iso_datetime as string;
        }
        return smallNotif;
      },
      {}
    ) || {};
  const disputableCases = completeCases?.filter((c) =>
    Object.keys(smallNotif).includes(c.short_id)
  );

  const mappedDisputableCases: Case[] = (disputableCases || []).map(
    (caseItem) => ({
      short_id: caseItem.short_id,
      customer_name: caseItem.customer_name,
      received_date: smallNotif[caseItem.short_id] || "",
      expiry_date:
        addDays(new Date(smallNotif[caseItem.short_id]), 2).toString() || null,
      is_started: false as boolean,
      check_set: [
        {
          type: "AUSTRALIAN_DCO",
          status: "APPLICANT_ACTION_REQUIRED" as const,
        },
      ] as const,
      applicant_status: caseItem.applicant_status,
      restrict_applicant_communications: false as boolean,
      has_outstanding_information_requests: false,
      outstanding_information_request_item_count: 0,
    })
  );

  if (outstandingRequestsLoading) {
    return <Loader type="spinner" size="default" />;
  }

  if (
    !openCases?.length &&
    !mappedDisputableCases?.length &&
    !informationRequestCards.length
  ) {
    return (
      <div>
        <AppTitle
          title={fullGreeting}
          subtitle={t("You're all caught up.")}
          className={styles.title}
        />
        <EmptyState
          illustration="check-todo"
          description={
            <Trans>
              When someone sends you a background check, it will appear here.
            </Trans>
          }
        />
      </div>
    );
  }

  const activeCases: Case[] = [
    ...(openCases || []),
    ...mappedDisputableCases,
  ].filter((item) => item.applicant_status !== "APPLICANT_EXPIRED");

  const allCases: Case[] = [...(openCases || []), ...mappedDisputableCases];
  const sortedCases = allCases.sort(compareActiveCases);

  const handlePress = async (instance: Case) => {
    if (activeCaseStatus.includes(instance.applicant_status)) {
      navigate(`/apply/${instance.short_id}/`);
      return;
    }

    if (instance.applicant_status === "APPLICANT_SUBMITTED") {
      navigate(`/${instance.short_id}/dco-review`);
      return;
    }

    try {
      await request(
        `/api/internal/applicant/case/${instance.short_id}/archive/`,
        {
          method: "POST",
        }
      );
      await refreshOpenCases();
      await refreshCompleteCases();
    } catch (e) {
      console.error("Error archiving application: ", e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
    }
  };
  const toCompleteCount = activeCases.length + informationRequestCards.length;
  const singularText = informationRequestCards.length
    ? t("request")
    : t("background check");
  const pluralText = informationRequestCards.length
    ? t("requests")
    : t("background checks");
  const backgroundCheckText = toCompleteCount === 1 ? singularText : pluralText;

  return (
    <section aria-label="applications">
      <AppTitle
        title={fullGreeting}
        subtitle={t("You have {{num}} {{backgroundCheckText}} to complete.", {
          num: toCompleteCount,
          backgroundCheckText,
        })}
        className={styles.title}
      />
      {informationRequestCards.map(({ caseItem, requests }) => {
        const itemCount = countUnsubmittedItems(requests);
        const sentAt = getOldestSentAt(requests);
        return (
          <PortalCard
            key={`information-request-${caseItem.short_id}`}
            applicantId={caseItem.short_id}
            title={caseItem.customer_name}
            type="information-request"
            subtitle={t("Information request")}
            details={
              itemCount === 1
                ? t("1 information request")
                : t("{{num}} information requests", { num: itemCount })
            }
            receivedDate={sentAt ? new Date(sentAt) : new Date()}
            onPress={() =>
              navigate(informationRequestPagePath(caseItem.short_id))
            }
            teamName={caseItem.customer_name}
            checksOrdered={[]}
          />
        );
      })}
      {sortedCases.map(
        (
          { short_id, customer_name, received_date, expiry_date, check_set },
          i
        ) => (
          <PortalCard
            key={i}
            applicantId={short_id}
            title={customer_name ?? "-"}
            type={getCardType(sortedCases[i])}
            expirationDate={expiry_date ? new Date(expiry_date) : undefined}
            receivedDate={new Date(received_date)}
            onPress={() => handlePress(sortedCases[i])}
            teamName={customer_name}
            checksOrdered={check_set}
            disableDeclineCase={disableDeclineCase(
              check_set,
              checksThatCannotBeDeclined
            )}
          />
        )
      )}
    </section>
  );
}

const styles = {
  title: css({
    marginBottom: "var(--space-8)",
  }),
};
